// Which kinds of ticket Today is showing, kept between openings.
//
// A leaf with two importers — `pages/TodayPage.vue`, which owns the control,
// and `lib/share-pad.js`, which draws the picture. The picture has to follow
// the screen (you share what you are looking at, not what the app happens to
// hold), and the two of them reaching for the same ref is what makes that true
// by construction rather than by a prop somebody remembers to pass.
//
// It is NOT in `store.js` on purpose: nothing here is about the day's board,
// the pad or the money. It is a reading preference, in the same drawer as the
// leg picker's sort order — and `store.js` is the one file in the app that
// three slices at a time are always halfway through.

import { ref } from 'vue';
import { prefs } from '../../core/storage.js';
import { FEED_FILTER_ALL } from '../feed.js';

/** Where it lives on the device. */
export const FEED_FILTER_KEY = 'todayFeedFilter';

/**
 * Whatever is in storage, made safe.
 *
 * A missing, corrupt or half-written value reads as EVERYTHING. The default is
 * the whole feed in both directions: a punter who has never touched the control
 * must never find tickets missing, and neither must one whose phone dropped the
 * preference — the failure of a filter has to be "it forgot", never "it hid
 * something".
 */
function stored() {
  const raw = prefs[FEED_FILTER_KEY];
  if (!raw || typeof raw !== 'object') return { ...FEED_FILTER_ALL };
  return {
    sports: raw.sports !== false,
    racing: raw.racing !== false,
  };
}

/** The live filter: `{ sports, racing }`, both true by default. */
export const feedFilter = ref(stored());

/**
 * Turn one kind on or off and remember it.
 *
 * Both off is ALLOWED, and that is a decision. A toggle that refuses the last
 * tap is a control that lies about its own state — `aria-pressed` would say one
 * thing and the screen another — so the empty feed is a real place you can get
 * to, and Today prints the line that names what is hidden with a way back in
 * one tap. See `hiddenLine` in lib/feed.js.
 */
export function setFeedKind(kind, on) {
  if (kind !== 'sports' && kind !== 'racing') return feedFilter.value;
  feedFilter.value = { ...feedFilter.value, [kind]: !!on };
  try { prefs[FEED_FILTER_KEY] = { ...feedFilter.value }; } catch { /* private mode */ }
  return feedFilter.value;
}

/** Back to the whole feed — the way out of an empty screen. */
export const showEverything = () => {
  setFeedKind('sports', true);
  return setFeedKind('racing', true);
};
